import { type CardCaptureStatus, FaceStatus, useFaceDetection } from '@onefootprint/vision';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCountdown } from 'usehooks-ts';
import { REQUIRED_SUCCESSES, SELFIE_CHECK_INTERVAL, STATUS_CHANGE_DELAY } from '../../../constants';
import type { CaptureStatus, Resolution, VideoRef } from '../types';
type AutoCaptureFaceProps = {
  canvasRef: MutableRefObject<HTMLCanvasElement | undefined>;
  isCaptured: boolean;
  onDetectionComplete: () => void;
  onDetectionReset: () => void;
  onStatusChange: (currStatus: CaptureStatus | undefined) => void;
  outlineOffsetX?: number;
  outlineOffsetY?: number;
  outlineWidth: number;
  videoRef: VideoRef;
  videoSize: Resolution | undefined;
  videoResolution: Resolution | undefined;
};
const CountDownProps = {
  countStart: 3,
  intervalMs: STATUS_CHANGE_DELAY / 3
};
const isFaceOk = (x: unknown) => x === FaceStatus.OK;
const isNonZeroVideoSize = (v?: Resolution): v is Resolution => Boolean(v) && v?.width !== 0 && v?.height !== 0;
const useAutoCaptureFace = ({
  canvasRef,
  isCaptured,
  onDetectionComplete,
  onDetectionReset,
  onStatusChange,
  outlineOffsetX,
  outlineOffsetY,
  outlineWidth,
  videoRef,
  videoSize,
  videoResolution
}: AutoCaptureFaceProps) => {
  const successCountRef = useRef(0);
  const detectionCompletedRef = useRef(false); // guards against calling onDetectionComplete multiple times before a reset
  const nonOkDuringCountdownRef = useRef(0); // consecutive non-OK frames seen while countdown is active
  const pastStatusRef = useRef<FaceStatus | CardCaptureStatus | undefined>(FaceStatus.detecting);
  const [statusChangeDelayRunning, setStatusChangeDelayTimeRunning] = useState(false);
  const {
    getFaceStatus
  } = useFaceDetection();
  const [waitVal, {
    startCountdown,
    resetCountdown
  }] = useCountdown(CountDownProps);
  const detectAndCaptureFace = useCallback(async (): Promise<void> => {
    if (isCaptured || !videoRef.current || !canvasRef.current || !isNonZeroVideoSize(videoSize)) {
      return;
    }
    const refCanvas = canvasRef.current;
    const context = refCanvas?.getContext('2d', {
      willReadFrequently: true
    });
    if (!context) {
      return;
    }
    const faceStatus = await getFaceStatus(canvasRef, videoRef, {
      width: videoSize.width,
      height: videoSize.height,
      frameDimensions: {
        width: outlineWidth,
        height: outlineWidth,
        frameOffsetX: outlineOffsetX ?? 0,
        frameOffsetY: outlineOffsetY ?? 0
      },
      videoResolution
    });
    if (isFaceOk(faceStatus)) {
      successCountRef.current += 1;
      nonOkDuringCountdownRef.current = 0;
      resetCountdown();
      setStatusChangeDelayTimeRunning(false);
      if (successCountRef.current >= REQUIRED_SUCCESSES) {
        onStatusChange(FaceStatus.OK);
        if (!detectionCompletedRef.current) {
          detectionCompletedRef.current = true;
          onDetectionComplete();
        }
      }
    } else {
      successCountRef.current = 0;
      if (detectionCompletedRef.current) {
        detectionCompletedRef.current = false;
        nonOkDuringCountdownRef.current = 0;
        onStatusChange(faceStatus); // clear "Hold still…" feedback
        onDetectionReset();
      } else {
        nonOkDuringCountdownRef.current = 0;
        if (!statusChangeDelayRunning) {
          startCountdown();
          setStatusChangeDelayTimeRunning(true);
        }
      }
    }
    pastStatusRef.current = faceStatus;
  }, [canvasRef, getFaceStatus, isCaptured, onDetectionComplete, onDetectionReset, onStatusChange, outlineOffsetX, outlineOffsetY, outlineWidth, resetCountdown, startCountdown, statusChangeDelayRunning, videoRef, videoSize]);
  useEffect(() => {
    if (waitVal === 0) {
      if (!isFaceOk(pastStatusRef.current)) {
        onStatusChange(pastStatusRef.current);
        successCountRef.current = 0;
        detectionCompletedRef.current = false;
        resetCountdown();
        setStatusChangeDelayTimeRunning(false);
        onDetectionReset();
      }
    }
  }, [waitVal, onStatusChange, resetCountdown, onDetectionReset]);
  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (isCaptured) {
        clearInterval(intervalId);
        return;
      }
      await detectAndCaptureFace();
    }, SELFIE_CHECK_INTERVAL);
    return () => clearInterval(intervalId);
  }, [detectAndCaptureFace, isCaptured]);
};
export default useAutoCaptureFace;